/**
 * What the paywall says when the App Store or Google Play refuses an order (MUS-617). The store's
 * own text is never shown: on Android it is the Play response code's name ("ITEM_UNAVAILABLE"),
 * on iOS Apple's description of the StoreKit error.
 */

export const STORE_ORDER_FALLBACK =
  'The store did not complete the purchase. Please try again, and if it keeps happening, contact support@museflow.ai.';

export const STORE_PLAN_NOT_ON_THIS_ACCOUNT =
  "This plan is not available on this store account. Check that your store account's country or region is where you live, or switch to another store account, then try again.";

export const STORE_SUBSCRIPTION_ON_ANOTHER_ACCOUNT =
  'Your store account may already have a MuseFlow subscription on a different MuseFlow account. Log out and log in with that account to use it.';

export const STORE_ALREADY_SUBSCRIBED =
  'Your store account already has this subscription. Tap Restore purchases, or log in with the MuseFlow account that bought it.';

export const STORE_UNREACHABLE =
  'We could not reach the store. Check your connection and try again.';

export const STORE_CANNOT_TAKE_PAYMENTS =
  'The store cannot take payments on this device right now. Check that you are signed in to the store, then try again.';

export const STORE_NO_SUBSCRIPTIONS =
  'The store on this device does not support subscriptions. Update the store app, then try again.';

export const STORE_PURCHASES_NOT_ALLOWED =
  "This store account is not allowed to make purchases. Check the store's payment settings and any parental controls, then try again.";

export const STORE_PURCHASES_BLOCKED =
  "Purchases are blocked on this device. Check the device's settings and any parental controls, then try again.";

export const STORE_PAYMENT_INVALID =
  'The store could not accept this payment. Check your payment method in the store, then try again.';

export const STORE_PAYMENT_EXPIRED = 'The payment took too long and expired. Please try again.';

export const STORE_PRIVACY_TERMS =
  "Please open the App Store and accept Apple's latest privacy terms, then try again.";

export const STORE_CANNOT_CHANGE_SUBSCRIPTION =
  "This subscription cannot be changed here. Please manage it in your store's subscriptions.";

/** Sentences the app already shows elsewhere for the same situations. */
export const STORE_UNAVAILABLE = 'The store is unavailable. Please try again.';
export const STORE_OFFER_UNAVAILABLE = 'This subscription is unavailable. Please reload prices.';

/**
 * cordova-plugin-purchase's ErrorCode values (CdvPurchase.ErrorCode, 6777000 + n). They are fixed
 * by the plugin's native code on both platforms, so they are written here rather than read from
 * the plugin at run time.
 */
export const STORE_ERROR_CODE = {
  SETUP: 6777001,
  LOAD: 6777002,
  PURCHASE: 6777003,
  LOAD_RECEIPTS: 6777004,
  CLIENT_INVALID: 6777005,
  PAYMENT_CANCELLED: 6777006,
  PAYMENT_INVALID: 6777007,
  PAYMENT_NOT_ALLOWED: 6777008,
  UNKNOWN: 6777010,
  REFRESH_RECEIPTS: 6777011,
  INVALID_PRODUCT_ID: 6777012,
  FINISH: 6777013,
  COMMUNICATION: 6777014,
  SUBSCRIPTIONS_NOT_AVAILABLE: 6777015,
  MISSING_TOKEN: 6777016,
  VERIFICATION_FAILED: 6777017,
  BAD_RESPONSE: 6777018,
  REFRESH: 6777019,
  PAYMENT_EXPIRED: 6777020,
  DOWNLOAD: 6777021,
  SUBSCRIPTION_UPDATE_NOT_AVAILABLE: 6777022,
  PRODUCT_NOT_AVAILABLE: 6777023,
  CLOUD_SERVICE_PERMISSION_DENIED: 6777024,
  CLOUD_SERVICE_NETWORK_CONNECTION_FAILED: 6777025,
  CLOUD_SERVICE_REVOKED: 6777026,
  PRIVACY_ACKNOWLEDGEMENT_REQUIRED: 6777027,
  UNAUTHORIZED_REQUEST_DATA: 6777028,
  INVALID_OFFER_IDENTIFIER: 6777029,
  INVALID_OFFER_PRICE: 6777030,
  INVALID_SIGNATURE: 6777031,
  MISSING_OFFER_PARAMS: 6777032,
  STORE_BLOCKED: 6777033,
  VALIDATOR_SUBSCRIPTION_EXPIRED: 6778003,
} as const;

/**
 * The sentence for each plugin code. A code missing here (setup, receipt, download and validator
 * codes an order does not return) gets the fallback.
 */
const SENTENCE_FOR_CODE: ReadonlyMap<number, string> = new Map<number, string>([
  [STORE_ERROR_CODE.SETUP, STORE_UNAVAILABLE],
  [STORE_ERROR_CODE.LOAD, STORE_UNAVAILABLE],
  [STORE_ERROR_CODE.CLIENT_INVALID, STORE_PURCHASES_NOT_ALLOWED],
  [STORE_ERROR_CODE.PAYMENT_NOT_ALLOWED, STORE_PURCHASES_NOT_ALLOWED],
  [STORE_ERROR_CODE.PAYMENT_INVALID, STORE_PAYMENT_INVALID],
  [STORE_ERROR_CODE.PAYMENT_EXPIRED, STORE_PAYMENT_EXPIRED],
  [STORE_ERROR_CODE.INVALID_PRODUCT_ID, STORE_OFFER_UNAVAILABLE],
  [STORE_ERROR_CODE.INVALID_OFFER_IDENTIFIER, STORE_OFFER_UNAVAILABLE],
  [STORE_ERROR_CODE.INVALID_OFFER_PRICE, STORE_OFFER_UNAVAILABLE],
  [STORE_ERROR_CODE.INVALID_SIGNATURE, STORE_OFFER_UNAVAILABLE],
  [STORE_ERROR_CODE.MISSING_OFFER_PARAMS, STORE_OFFER_UNAVAILABLE],
  [STORE_ERROR_CODE.PRODUCT_NOT_AVAILABLE, STORE_PLAN_NOT_ON_THIS_ACCOUNT],
  [STORE_ERROR_CODE.COMMUNICATION, STORE_UNREACHABLE],
  [STORE_ERROR_CODE.CLOUD_SERVICE_NETWORK_CONNECTION_FAILED, STORE_UNREACHABLE],
  [STORE_ERROR_CODE.SUBSCRIPTIONS_NOT_AVAILABLE, STORE_NO_SUBSCRIPTIONS],
  [STORE_ERROR_CODE.SUBSCRIPTION_UPDATE_NOT_AVAILABLE, STORE_CANNOT_CHANGE_SUBSCRIPTION],
  [STORE_ERROR_CODE.PRIVACY_ACKNOWLEDGEMENT_REQUIRED, STORE_PRIVACY_TERMS],
  [STORE_ERROR_CODE.STORE_BLOCKED, STORE_PURCHASES_BLOCKED],
]);

/**
 * Google Play's BillingResponseCode names. The plugin's Android code reports a refused Play
 * purchase as PURCHASE (or PAYMENT_CANCELLED) with the response code's name as the whole message,
 * and an unnamed response code as "CODE_<number>".
 */
const SENTENCE_FOR_PLAY_RESPONSE: ReadonlyMap<string, string> = new Map<string, string>([
  ['ITEM_UNAVAILABLE', STORE_PLAN_NOT_ON_THIS_ACCOUNT],
  // Seen when the Play account already holds a subscription bought under another MuseFlow account.
  ['DEVELOPER_ERROR', STORE_SUBSCRIPTION_ON_ANOTHER_ACCOUNT],
  ['ITEM_ALREADY_OWNED', STORE_ALREADY_SUBSCRIBED],
  ['BILLING_UNAVAILABLE', STORE_CANNOT_TAKE_PAYMENTS],
  ['FEATURE_NOT_SUPPORTED', STORE_NO_SUBSCRIPTIONS],
  ['SERVICE_DISCONNECTED', STORE_UNREACHABLE],
  ['SERVICE_TIMEOUT', STORE_UNREACHABLE],
  ['SERVICE_UNAVAILABLE', STORE_UNREACHABLE],
  ['NETWORK_ERROR', STORE_UNREACHABLE],
  // BillingResponseCode.NETWORK_ERROR (12) has no name in the plugin.
  ['CODE_12', STORE_UNREACHABLE],
]);

/** store.order() answers this when the store was not set up; it is the app's state, not a refusal. */
const ADAPTER_NOT_READY = /^Adapter not found or not ready/;

export interface StoreOrderError {
  code?: string | number;
  message?: string;
}

/** The sentence the paywall shows for a store's refusal of an order. Never the store's own text. */
export function storeOrderErrorMessage(error: StoreOrderError): string {
  const message = typeof error.message === 'string' ? error.message.trim() : '';
  const playSentence = SENTENCE_FOR_PLAY_RESPONSE.get(message);
  if (playSentence) return playSentence;
  if (ADAPTER_NOT_READY.test(message)) return STORE_UNAVAILABLE;
  return SENTENCE_FOR_CODE.get(Number(error.code)) ?? STORE_ORDER_FALLBACK;
}

/** The store's own code and text, for error reporting, so a refusal can still be searched for. */
export function storeOrderErrorReport(error: StoreOrderError): string {
  return `Store order refused: ${error.message || 'no message'} (code ${error.code ?? 'none'})`;
}
